import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  Clock,
  Calendar,
  User,
  ChevronRight,
  MessageCircle,
  AlertCircle,
  CheckCircle,
  XCircle,
  CalendarPlus,
  Search,
  Activity,
  ArrowUpRight,
  BarChart3,
  TrendingUp,
  Zap,
  ChevronDown,
  Mail
} from 'lucide-react';
import { STAGE_COLORS } from '../../lib/constants';
import { getWhatsappLink, formatDate, isWonStage } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { generateCallCalendarUrl, openInGoogleCalendar } from '../../utils/googleCalendar';
import { useGoogleCalendar } from '../../contexts/GoogleCalendarContext';
import { LogCallModal } from '../modals';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';

// ─── Call outcome options ─────────────────────────────────────────────────────
const CALL_OUTCOMES = [
  { id: 'connected_interested',     label: 'Connected - Interested',          labelUr: 'رابطہ ہوا - دلچسپی ہے',       icon: CheckCircle, color: 'green'  },
  { id: 'connected_not_interested', label: 'Connected - Not Interested',      labelUr: 'رابطہ ہوا - دلچسپی نہیں',    icon: XCircle,     color: 'red'    },
  { id: 'connected_callback',       label: 'Connected - Callback Requested',  labelUr: 'رابطہ ہوا - دوبارہ کال کریں', icon: PhoneCall,   color: 'blue'   },
  { id: 'no_answer',                label: 'No Answer',                       labelUr: 'جواب نہیں',                   icon: PhoneMissed, color: 'amber'  },
  { id: 'busy',                     label: 'Busy / Call Later',               labelUr: 'مصروف / بعد میں کال کریں',   icon: Clock,       color: 'orange' },
  { id: 'wrong_number',             label: 'Wrong Number',                    labelUr: 'غلط نمبر',                    icon: PhoneOff,    color: 'gray'   },
  { id: 'conducted_success',        label: 'Conducted Successfully',          labelUr: 'کامیابی سے منعقد ہوا',         icon: CheckCircle, color: 'green'  },
  { id: 'rescheduled',              label: 'Rescheduled',                     labelUr: 'دوبارہ شیڈول کیا گیا',         icon: Clock,       color: 'orange' },
  { id: 'client_absent',            label: 'Client Absent / No Show',         labelUr: 'کلائنٹ غیر حاضر',             icon: XCircle,     color: 'gray'   },
  { id: 'cancelled',                label: 'Cancelled',                       labelUr: 'منسوخ ہو گیا',                 icon: XCircle,     color: 'red'    },
  { id: 'sent',                     label: 'Sent',                            labelUr: 'بھیج دیا گیا',                 icon: CheckCircle, color: 'blue'   },
  { id: 'replied',                  label: 'Replied',                         labelUr: 'جواب دیا',                     icon: CheckCircle, color: 'green'  },
  { id: 'bounced',                  label: 'Bounced / Failed',                labelUr: 'ناکام ہو گیا',                 icon: XCircle,     color: 'red'    },
  { id: 'submitted',                label: 'Submitted / Sent',                labelUr: 'جمع کرایا گیا',                 icon: CheckCircle, color: 'blue'   },
  { id: 'accepted',                 label: 'Accepted',                        labelUr: 'قبول کر لیا گیا',               icon: CheckCircle, color: 'green'  },
  { id: 'rejected',                 label: 'Rejected',                        labelUr: 'مسترد کر دیا گیا',              icon: XCircle,     color: 'red'    }
];

// ─── Avatar ───────────────────────────────────────────────────────────────────
function LeadAvatar({ name, priority }) {
  const initials = (name || '?')
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  const gradient = {
    overdue:  'from-red-500 to-rose-600',
    new:      'from-amber-400 to-orange-500',
    today:    'from-blue-500 to-indigo-600',
    upcoming: 'from-slate-400 to-slate-500',
  }[priority] || 'from-slate-400 to-slate-500';

  return (
    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0 shadow-sm`}>
      <span className="text-white text-xs font-bold tracking-wide">{initials}</span>
    </div>
  );
}

// ─── Priority badge ───────────────────────────────────────────────────────────
function PriorityBadge({ priority, label }) {
  const style = {
    overdue:  'bg-red-50 text-red-600 border border-red-200',
    new:      'bg-amber-50 text-amber-600 border border-amber-200',
    today:    'bg-blue-50 text-blue-600 border border-blue-200',
    upcoming: 'bg-slate-50 text-slate-500 border border-slate-200',
  }[priority] || 'bg-slate-50 text-slate-500 border border-slate-200';

  const icon = {
    overdue:  <AlertCircle className="w-3 h-3" />,
    new:      <Zap className="w-3 h-3" />,
    today:    <Clock className="w-3 h-3" />,
    upcoming: <Calendar className="w-3 h-3" />,
  }[priority];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${style}`}>
      {icon}
      {label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function FollowUpView({
  prequalifications,
  tenders = [],
  profiles = [],
  currentUser,
  isLoading,
  onLogCall,  
  onSelectLead,
  onSaveLead,
  venues,
  eventTypes,
  managers,
  onConvertProfile,
}) {
  const { t, language } = useLanguage();
  useGoogleCalendar(); // keep context subscription alive
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || 'all';
  const [filterType, setFilterType] = useState(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [managerFilter, setManagerFilter] = useState('All');
  const [entityTypeFilter, setEntityTypeFilter] = useState('All');
  const [displayLimit, setDisplayLimit] = useState(50);
  const navigate = useNavigate();

  // Today as YYYY-MM-DD (local time)
  const today =
    new Date().getFullYear() +
    '-' + String(new Date().getMonth() + 1).padStart(2, '0') +
    '-' + String(new Date().getDate()).padStart(2, '0');

  const [currentTime, setCurrentTime] = useState(Date.now());

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  // ── Categorise prequalifications ────────────────────────────────────────────────────────
  const callList = useMemo(() => {
    let userLeads = (prequalifications || []).map(p => ({
      ...p,
      uiType: 'Prequalification'
    }));
    
    // Map Tenders to a format similar to Prequalifications
    const mappedTenders = (tenders || []).map(t => ({
      ...t,
      originalId: t.id,
      id: `tender_${t.id}`, // Avoid ID collisions
      isTender: true,
      uiType: t.type === 'PROPOSAL' ? 'Proposal' : 'Tender',
      clientName: t.profile?.clientName || t.title || 'Tender',
      phone: t.profile?.mobile,
      manager: t.assignee?.username,
      stage: t.status, // e.g. 'In Process'
      nextCallDate: t.nextCallDate || (t.followUpDate ? t.followUpDate.split('T')[0] : null),
      nextCallTime: t.nextCallTime || (t.followUpDate ? new Date(t.followUpDate).toTimeString().substring(0, 5) : null),
      lastCallOutcome: t.lastCallOutcome || null,
      lastCallDate: t.lastCallDate || null,
      createdAt: t.createdAt
    }));
    
    // Map Profiles to a format similar to Prequalifications
    const mappedProfiles = (profiles || []).map(p => ({
      ...p,
      originalId: p.id,
      id: `profile_${p.id}`, // Avoid ID collisions
      isProfile: true,
      uiType: 'Profile',
      clientName: p.clientName || p.contactPersonName || 'No Name',
      phone: p.mobile,
      manager: p.assignedTo?.username || p.assignee?.username || 'None',
      stage: p.status || 'New',
      nextCallDate: p.nextCallDate || null,
      nextCallTime: p.nextCallTime || null,
      lastCallOutcome: p.lastCallOutcome || null,
      lastCallDate: p.lastCallDate || null,
      createdAt: p.createdAt
    }));

    let allLeads = [...userLeads, ...mappedTenders, ...mappedProfiles];

    if (managerFilter !== 'All') {
      allLeads = allLeads.filter(l => l.manager === managerFilter);
    }

    if (entityTypeFilter !== 'All') {
      allLeads = allLeads.filter(l => l.uiType === entityTypeFilter);
    }

    const isValidDateStr = (d) => d && d !== '-';
    const activeLeads = allLeads.filter(
      l => {
        // Approval check: Only approved items (or legacy items without approvalStatus) enter the follow-up list
        if (l.approvalStatus === 'PENDING_APPROVAL' || l.approvalStatus === 'REJECTED') {
          return false;
        }

        return (
          l.stage !== 'Enlisted' && l.stage !== 'Disqualified' &&
          (l.isTender || l.isProfile || l.inCallList === true || l.stage === 'Call List' || l.stage === 'Site Visited' || l.stage === 'New' || l.stage === 'Submitted' || l.stage === 'Pending' || !!l.nextCallDate || (isValidDateStr(l.siteVisitDate) && !l.siteVisited)) &&
          !isWonStage(l.stage) &&
          l.stage !== 'Lost' &&
          !(l.isProfile && (l.stage === 'Interested' || l.lastCallOutcome === 'connected_interested' || !!l.prequalificationId)) &&
          !(l.uiType === 'Prequalification' && !!l.tenderId)
        );
      }
    );

    const overdue = [];
    const todayCalls = [];
    const upcoming = [];

    activeLeads.forEach(prequalification => {
      const hasNextCall = !!prequalification.nextCallDate;
      const hasSiteVisit = isValidDateStr(prequalification.siteVisitDate);
      const targetDate = hasNextCall ? prequalification.nextCallDate : (hasSiteVisit ? prequalification.siteVisitDate : null);

      if (!targetDate) {
        const createdAtTime = new Date(prequalification.createdAt || 0).getTime();
        const ms24h = 24 * 60 * 60 * 1000;
        if (currentTime - createdAtTime > ms24h) {
          overdue.push({ ...prequalification, callPriority: 'overdue', callStatus: t('overdue') + ': Not contacted within 24 hrs' });
        } else {
          let callStatusStr = t('newLeadNeverCalled');
          if (prequalification.uiType === 'Tender') {
             callStatusStr = language === 'ur' ? 'نیا ٹینڈر - کبھی فالو اپ نہیں ہوا' : 'New Tender - Never Followed up';
          } else if (prequalification.uiType === 'Proposal') {
             callStatusStr = language === 'ur' ? 'نیا پروپوزل - کبھی فالو اپ نہیں ہوا' : 'New Proposal - Never Followed up';
          } else if (prequalification.isProfile) {
             callStatusStr = language === 'ur' ? 'نیا پروفائل - کبھی فالو اپ نہیں ہوا' : 'New Profile - Never Followed up';
          }
          todayCalls.push({ ...prequalification, callPriority: 'new', callStatus: callStatusStr });
        }
        return;
      }

      const nextDateStr = targetDate.split('T')[0];
      let isOverdue = false;
      let isToday = false;
      
      let extractedSiteVisitTime = prequalification.siteVisitTime;
      if (!extractedSiteVisitTime && prequalification.siteVisitDate) {
        try {
          const d = new Date(prequalification.siteVisitDate);
          if (!isNaN(d.getTime())) {
            extractedSiteVisitTime = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
          }
        } catch (e) {}
      }
      
      const targetTime = hasNextCall ? prequalification.nextCallTime : extractedSiteVisitTime;
      const isSiteVisitFallback = !hasNextCall && hasSiteVisit;

      if (nextDateStr < today) {
        isOverdue = true;
      } else if (nextDateStr === today) {
        if (targetTime) {
          const scheduled = new Date(`${nextDateStr}T${targetTime}:00`);
          if (!isNaN(scheduled.getTime()) && currentTime - scheduled.getTime() > 24 * 60 * 60 * 1000) {
            isOverdue = true;
          } else {
            isToday = true;
          }
        } else {
          isToday = true;
        }
      }

      const statusPrefix = isSiteVisitFallback ? (language === 'ur' ? 'سائٹ وزٹ' : 'Site Visit') : '';

      if (isOverdue) {
        overdue.push({
          ...prequalification,
          callPriority: 'overdue',
          callStatus: isSiteVisitFallback
            ? `${t('overdue')} ${statusPrefix}: ${formatDate(nextDateStr)}${targetTime ? ` ${targetTime}` : ''}`
            : `${t('overdue')}: ${formatDate(nextDateStr)}${targetTime ? ` ${targetTime}` : ''}`,
        });
      } else if (isToday) {
        todayCalls.push({
          ...prequalification,
          callPriority: 'today',
          callStatus: isSiteVisitFallback
             ? `${statusPrefix} ${t('today')}${targetTime ? ` ${t('at')} ${targetTime}` : ''}`
             : (targetTime ? `${t('todayAt')} ${targetTime}` : t('today')),
        });
      } else {
        upcoming.push({
          ...prequalification,
          callPriority: 'upcoming',
          callStatus: isSiteVisitFallback
             ? `${statusPrefix}: ${formatDate(nextDateStr)}${targetTime ? ` ${t('at')} ${targetTime}` : ''}`
             : `${formatDate(nextDateStr)}${targetTime ? ` ${t('at')} ${targetTime}` : ''}`,
        });
      }
    });

    const getTargetDate = (l) => !!l.nextCallDate ? l.nextCallDate : (isValidDateStr(l.siteVisitDate) ? l.siteVisitDate : 0);
    const getTargetTime = (l) => {
      if (!!l.nextCallDate) return l.nextCallTime;
      if (!isValidDateStr(l.siteVisitDate)) return null;
      if (l.siteVisitTime) return l.siteVisitTime;
      try {
        const d = new Date(l.siteVisitDate);
        if (!isNaN(d.getTime())) {
          return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
        }
      } catch (e) {}
      return null;
    };

    overdue.sort((a, b) => new Date(getTargetDate(a)) - new Date(getTargetDate(b)));
    todayCalls.sort((a, b) => {
      const aTime = getTargetTime(a);
      const bTime = getTargetTime(b);
      if (!aTime && !bTime) return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime.localeCompare(bTime);
    });
    upcoming.sort((a, b) => {
      const d = new Date(getTargetDate(a)) - new Date(getTargetDate(b));
      if (d !== 0) return d;
      const aTime = getTargetTime(a);
      const bTime = getTargetTime(b);
      if (!aTime) return 1;
      if (!bTime) return -1;
      return aTime.localeCompare(bTime);
    });

    return { overdue, today: todayCalls, upcoming };
  }, [prequalifications, tenders, profiles, currentUser, today, t, currentTime, managerFilter, entityTypeFilter, language]);

  // ── Filter + search ─────────────────────────────────────────────────────────
  const handleFilterChange = type => {
    setFilterType(type);
    setDisplayLimit(50);
  };

  const filteredList = useMemo(() => {
    switch (filterType) {
      case 'overdue':  return callList.overdue;
      case 'today':    return callList.today;
      case 'upcoming': return callList.upcoming;
      default:         return [...callList.overdue, ...callList.today, ...callList.upcoming];
    }
  }, [callList, filterType]);

  const searchedList = useMemo(() => {
    if (!searchQuery.trim()) return filteredList;
    const q = searchQuery.toLowerCase();
    return filteredList.filter(
      l =>
        (l.clientName || '').toLowerCase().includes(q) ||
        (l.phone || l.profile?.mobile || '').toLowerCase().includes(q) ||
        (l.stage || '').toLowerCase().includes(q) ||
        (l.manager || '').toLowerCase().includes(q) ||
        (l.title || '').toLowerCase().includes(q) ||
        (l.projectName || l.profile?.projectName || '').toLowerCase().includes(q) ||
        (l.profile?.email || '').toLowerCase().includes(q) ||
        (l.referenceNumber || '').toLowerCase().includes(q)
    );
  }, [filteredList, searchQuery]);

  // ── Stats ───────────────────────────────────────────────────────────────────
  const stats = {
    overdue:  callList.overdue.length,
    today:    callList.today.length,
    upcoming: callList.upcoming.length,
    total:    callList.overdue.length + callList.today.length + callList.upcoming.length,
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getPriorityLabel = (priority, uiType) => {
    if (priority === 'new') {
      if (uiType === 'Tender') return language === 'ur' ? 'نیا ٹینڈر' : 'New Tender';
      if (uiType === 'Proposal') return language === 'ur' ? 'نیا پروپوزل' : 'New Proposal';
      if (uiType === 'Profile') return language === 'ur' ? 'نیا پروفائل' : 'New Profile';
      return language === 'ur' ? 'نیا' : 'New Prequalification';
    }
    return {
      overdue:  language === 'ur' ? 'تاخیر'     : 'Overdue',
      today:    language === 'ur' ? 'آج'         : 'Today',
      upcoming: language === 'ur' ? 'آنے والا'   : 'Upcoming',
    }[priority] || '';
  };

  const getRowAccent = priority => ({
    overdue:  'border-l-[3px] border-l-red-400 hover:bg-red-50/40',
    new:      'border-l-[3px] border-l-amber-400 hover:bg-amber-50/40',
    today:    'border-l-[3px] border-l-blue-400 hover:bg-blue-50/40',
    upcoming: 'border-l-[3px] border-l-slate-200 hover:bg-slate-50/60',
  }[priority] || 'hover:bg-slate-50/60');

  const getTabLabel = () => ({
    overdue:  language === 'ur' ? 'تاخیر سے کالیں'  : 'Overdue Follow ups',
    today:    language === 'ur' ? 'آج کی کالیں'      : "Today's Follow ups",
    upcoming: language === 'ur' ? 'آنے والی کالیں'   : 'Upcoming Follow ups',
  }[filterType] || (language === 'ur' ? 'تمام کالیں' : 'All Follow ups'));

  // ── Stat card definitions ───────────────────────────────────────────────────
  const statCards = [
    {
      key: 'all',
      label:           language === 'ur' ? 'کل'         : 'Total Follow ups',
      value:           stats.total,
      Icon:            BarChart3,
      activeGradient:  'from-slate-700 to-slate-900',
      inactiveGradient:'from-white to-slate-50',
      inactiveBorder:  'border-slate-200',
      inactiveText:    'text-slate-700',
      inactiveIcon:    'text-slate-500',
      inactiveIconBg:  'bg-slate-100',
      pulse:           false,
    },
    {
      key: 'overdue',
      label:           language === 'ur' ? 'تاخیر'      : 'Overdue',
      value:           stats.overdue,
      Icon:            AlertCircle,
      activeGradient:  'from-red-500 to-rose-600',
      inactiveGradient:'from-white to-red-50',
      inactiveBorder:  'border-red-100',
      inactiveText:    'text-red-700',
      inactiveIcon:    'text-red-500',
      inactiveIconBg:  'bg-red-100',
      pulse:           stats.overdue > 0,
    },
    {
      key: 'today',
      label:           language === 'ur' ? 'آج'         : "Today's Follow ups",
      value:           stats.today,
      Icon:            Clock,
      activeGradient:  'from-blue-500 to-indigo-600',
      inactiveGradient:'from-white to-blue-50',
      inactiveBorder:  'border-blue-100',
      inactiveText:    'text-blue-700',
      inactiveIcon:    'text-blue-500',
      inactiveIconBg:  'bg-blue-100',
      pulse:           false,
    },
    {
      key: 'upcoming',
      label:           language === 'ur' ? 'آنے والے'   : 'Upcoming',
      value:           stats.upcoming,
      Icon:            Calendar,
      activeGradient:  'from-emerald-500 to-teal-600',
      inactiveGradient:'from-white to-emerald-50',
      inactiveBorder:  'border-emerald-100',
      inactiveText:    'text-emerald-700',
      inactiveIcon:    'text-emerald-500',
      inactiveIconBg:  'bg-emerald-100',
      pulse:           false,
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-5 min-h-0 max-h-[calc(100dvh-7rem)]">

      {/* ── Page Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 flex-shrink-0">
        <div>
          <div className="flex items-center gap-3">
              {/* <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-200">
                <PhoneCall className="w-4 h-4 text-white" />
              </div> */}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
                {language === 'ur' ? 'کال لسٹ' : 'Follow up List'}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {stats.overdue > 0
                  ? (language === 'ur'
                    ? `${stats.overdue} تاخیر · ${stats.today} آج · ${stats.upcoming} آنے والے`
                    : `${stats.overdue} overdue · ${stats.today} today · ${stats.upcoming} upcoming`)
                  : (language === 'ur'
                    ? `${stats.today} آج · ${stats.upcoming} آنے والے`
                    : `${stats.today} scheduled today · ${stats.upcoming} upcoming`)}
              </p>
            </div>
          </div>
        </div>

        {/* Overdue alert pill */}
        {stats.overdue > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 bg-red-50 border border-red-200 rounded-lg text-xs font-semibold text-red-600 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            {stats.overdue} {language === 'ur' ? 'تاخیر' : 'overdue'}
          </div>
        )}
      </div>

      {/* ── Stat Cards ───────────────────────────────────────────────────────── */}
      <div className={`grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 flex-shrink-0 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>
        {isLoading && stats.total === 0 ? (
          [1, 2, 3, 4].map(i => (
            <div key={i} className="p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center gap-2.5 sm:block">
              <Skeleton width={32} height={32} animation="wave" className="shrink-0 sm:mb-2" />
              <div className="flex-1">
                <Skeleton width={50} height={14} animation="wave" className="mb-1" />
                <Skeleton width={35} height={12} animation="wave" />
              </div>
            </div>
          ))
        ) : (
          statCards.map(({ key, label, value, Icon, activeGradient, inactiveGradient, inactiveBorder, inactiveText, inactiveIcon, inactiveIconBg, pulse }) => {
            const isActive = filterType === key;
            return (
              <button
                key={key}
                onClick={() => handleFilterChange(key)}
                className={`relative p-2.5 sm:p-4 rounded-xl sm:rounded-2xl text-left transition-all duration-200 overflow-hidden ${
                  isActive
                    ? `bg-gradient-to-br ${activeGradient} text-white shadow-md sm:shadow-lg`
                    : `bg-gradient-to-br ${inactiveGradient} border ${inactiveBorder} hover:shadow-md hover:-translate-y-0.5`
                }`}
              >
                {isActive && <div className="absolute inset-0 bg-white/5 rounded-xl sm:rounded-2xl" />}
                {pulse && !isActive && (
                  <span className="absolute top-1.5 right-1.5 sm:top-3 sm:right-3 flex h-1.5 w-1.5 sm:h-2 sm:w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-red-500" />
                  </span>
                )}
                <div className="flex items-center gap-2.5 sm:block">
                  <div className="flex items-center justify-between sm:mb-3">
                    <div className={`w-8 h-8 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 ${isActive ? 'bg-white/20' : inactiveIconBg}`}>
                      <Icon className={`w-4 h-4 ${isActive ? 'text-white' : inactiveIcon}`} />
                    </div>
                    {isActive && <ArrowUpRight className="w-3.5 h-3.5 text-white/60 hidden sm:block" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-base sm:text-2xl font-bold leading-none sm:mb-1 ${isActive ? 'text-white' : inactiveText}`}>{value}</p>
                    <p className={`text-[11px] font-medium truncate mt-0.5 sm:mt-0 ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{label}</p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* ── Follow up List Panel ───────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">

        {/* Toolbar */}
        <div className="p-3 sm:px-4 sm:py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center justify-between sm:justify-start gap-2 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <PhoneCall className="w-3 h-3 text-white" />
              </div>
              <h2 className="font-semibold text-slate-800 text-sm truncate">{getTabLabel()}</h2>
              <span className="flex-shrink-0 px-2 py-0.5 bg-slate-200 text-slate-600 rounded-full text-[10px] font-bold">
                {searchedList.length}
              </span>
            </div>

            {/* Filter tabs */}
            <div className="flex items-center gap-0.5 sm:gap-1 bg-white border border-slate-200 rounded-lg p-0.5 flex-shrink-0">
              {[
                { key: 'all',      label: language === 'ur' ? 'سب'       : 'All'   },
                { key: 'overdue',  label: language === 'ur' ? 'تاخیر'    : 'Late'  },
                { key: 'today',    label: language === 'ur' ? 'آج'       : 'Today' },
                { key: 'upcoming', label: language === 'ur' ? 'آنے والے' : 'Soon'  },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleFilterChange(tab.key)}
                  className={`px-2 sm:px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
                    filterType === tab.key
                      ? 'bg-slate-800 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            {/* Entity Type Filter */}
            <div className="w-full sm:w-auto">
              <Dropdown className="w-full sm:w-auto">
                <DropdownTrigger>
                  <button className="w-full sm:min-w-[140px] bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs px-3 h-[34px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors text-slate-700">
                    <span className="font-medium truncate mr-2">
                      {entityTypeFilter === 'All' ? (language === 'ur' ? 'تمام اقسام' : 'All Types') : 
                       entityTypeFilter === 'Tender' ? (language === 'ur' ? 'ٹینڈر' : 'Tender') :
                       entityTypeFilter === 'Proposal' ? (language === 'ur' ? 'پروپوزل' : 'Proposal') :
                       entityTypeFilter === 'Profile' ? (language === 'ur' ? 'پروفائل' : 'Profile') :
                       (language === 'ur' ? 'پری کوالیفکیشن' : 'Prequalification')}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
                  <DropdownMenu
                    selectedKeys={new Set([entityTypeFilter])}
                    selectionMode="single"
                    onSelectionChange={(keys) => setEntityTypeFilter(Array.from(keys)[0])}
                  >
                    <DropdownItem id="All">{language === 'ur' ? 'تمام اقسام' : 'All Types'}</DropdownItem>
                    <DropdownItem id="Prequalification">{language === 'ur' ? 'پری کوالیفکیشن' : 'Prequalification'}</DropdownItem>
                    <DropdownItem id="Tender">{language === 'ur' ? 'ٹینڈر' : 'Tender'}</DropdownItem>
                    <DropdownItem id="Proposal">{language === 'ur' ? 'پروپوزل' : 'Proposal'}</DropdownItem>
                    <DropdownItem id="Profile">{language === 'ur' ? 'پروفائل' : 'Profile'}</DropdownItem>
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>

            {/* Manager Filter */}
            {['CEO', 'GM_Operations', 'BM_Operations', 'Senior_Team_Lead', 'Team_Lead'].includes(currentUser?.role) && (
              <div className="w-full sm:w-auto">
                <Dropdown className="w-full sm:w-auto">
                  <DropdownTrigger>
                    <button className="w-full sm:min-w-[140px] bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-xs px-3 h-[34px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors text-slate-700">
                      <span className="font-medium truncate mr-2">
                        {managerFilter === 'All' ? (language === 'ur' ? 'تمام سیلز' : 'All Assignee') : managerFilter}
                      </span>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    </button>
                  </DropdownTrigger>
                  <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
                    <DropdownMenu
                      selectedKeys={new Set([managerFilter])}
                      selectionMode="single"
                      onSelectionChange={(keys) => setManagerFilter(Array.from(keys)[0])}
                    >
                      <DropdownItem id="All">
                        {language === 'ur' ? 'تمام سیلز' : 'All Assignee'}
                      </DropdownItem>
                      {managers?.map(m => (
                        <DropdownItem key={m} id={m}>
                          {m}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>
              </div>
            )}

            {/* Search */}
            <div className="relative w-full sm:w-64 md:w-80">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={language === 'ur' ? 'پروجیکٹ یا کلائنٹ تلاش کریں...' : 'Search project or client'}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all h-[34px]"
              />
            </div>
          </div>
        </div>

        {/* List body */}
        <div className={`flex-1 overflow-y-auto ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>

          {/* Loading skeletons */}
          {isLoading && filteredList.length === 0 ? (
            <div>
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="px-4 py-3.5 border-b border-slate-50 flex items-center gap-3">
                  <Skeleton variant="rounded" width={40} height={40} animation="wave" className="rounded-xl flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton width={140} height={14} animation="wave" />
                    <Skeleton width={110} height={11} animation="wave" />
                    <Skeleton width={160} height={11} animation="wave" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton variant="rounded" width={32} height={32} animation="wave" className="rounded-lg" />
                    <Skeleton variant="rounded" width={32} height={32} animation="wave" className="rounded-lg" />
                    <Skeleton variant="rounded" width={72} height={32} animation="wave" className="rounded-lg" />
                  </div>
                </div>
              ))}
            </div>

          /* Empty state */
          ) : searchedList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-4 shadow-inner">
                <Phone className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-semibold text-slate-600 mb-1">
                {searchQuery
                  ? (language === 'ur' ? 'کوئی نتیجہ نہیں' : 'No results found')
                  : (language === 'ur' ? 'کوئی کال شیڈول نہیں' : 'No follow ups scheduled')}
              </p>
              <p className="text-xs text-slate-400">
                {searchQuery
                  ? (language === 'ur' ? 'مختلف الفاظ سے تلاش کریں' : 'Try a different search term')
                  : (language === 'ur' ? 'تمام کالیں مکمل ہو گئی ہیں' : 'All caught up — great work!')}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  {language === 'ur' ? 'صاف کریں' : 'Clear search'}
                </button>
              )}
            </div>

          /* Prequalification rows */
          ) : (
            <>
              {searchedList.slice(0, displayLimit).map(prequalification => {
                const stageColor = STAGE_COLORS[prequalification.stage] || STAGE_COLORS['New'];
                const outcomeObj = prequalification.lastCallOutcome
                  ? CALL_OUTCOMES.find(o => o.id === prequalification.lastCallOutcome)
                  : null;
                const friendlyOutcome = outcomeObj
                  ? (language === 'ur' ? outcomeObj.labelUr : outcomeObj.label)
                  : prequalification.lastCallOutcome;
                const lastCallDateStr = prequalification.lastCallDate
                  ? new Date(prequalification.lastCallDate).toLocaleString('en-PK', {
                      day: '2-digit', month: 'short',
                      hour: '2-digit', minute: '2-digit', hour12: true,
                    })
                  : null;
                const waLink = getWhatsappLink(prequalification.phone);

                return (
                  <div
                    key={prequalification.id}
                    className={`group px-3.5 sm:px-4 py-3.5 border-b border-slate-100 transition-all duration-150 ${getRowAccent(prequalification.callPriority)}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      {/* Avatar & Prequalification info */}
                      <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                        {/* Avatar */}
                        <LeadAvatar name={prequalification.clientName} priority={prequalification.callPriority} />

                        {/* Prequalification info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <span className="font-bold text-slate-900 text-sm sm:text-base truncate max-w-[200px] sm:max-w-none">
                              {prequalification.profile?.projectName || prequalification.clientName}
                            </span>
                            {prequalification.profile?.projectName && prequalification.clientName && prequalification.profile.projectName !== prequalification.clientName && (
                              <span className="text-sm text-slate-500 font-medium truncate max-w-[200px] sm:max-w-none">
                                ({prequalification.clientName})
                              </span>
                            )}

                            {/* Entity Type Badge */}
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                              prequalification.uiType === 'Tender' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                              prequalification.uiType === 'Proposal' ? 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' :
                              prequalification.uiType === 'Profile' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              'bg-slate-100 text-slate-700 border-slate-200'
                            }`}>
                              {prequalification.uiType === 'Tender' ? (language === 'ur' ? 'ٹینڈر' : 'Tender') :
                               prequalification.uiType === 'Proposal' ? (language === 'ur' ? 'پروپوزل' : 'Proposal') :
                               prequalification.uiType === 'Profile' ? (language === 'ur' ? 'پروفائل' : 'Profile') :
                               (language === 'ur' ? 'پری کوالیفکیشن' : 'Prequalification')}
                            </span>

                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${stageColor.bg} ${stageColor.text}`}>
                              {prequalification.stage}
                            </span>
                            <PriorityBadge priority={prequalification.callPriority} label={getPriorityLabel(prequalification.callPriority, prequalification.uiType)} />
                          </div>

                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            {prequalification.phone && (
                              <>
                                <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="text-xs text-slate-600 font-mono font-medium">{prequalification.phone}</span>
                              </>
                            )}
                            {prequalification.profile?.email && (
                              <>
                                <span className="text-slate-300">·</span>
                                <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="text-xs text-slate-600 font-medium break-all">{prequalification.profile.email}</span>
                              </>
                            )}
                            {prequalification.manager && (
                              <>
                                <span className="text-slate-300">·</span>
                                <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="text-xs text-slate-500 truncate max-w-[120px]">{prequalification.manager}</span>
                              </>
                            )}
                          </div>

                          <p className={`text-[11px] mt-1 font-medium ${
                            prequalification.callPriority === 'overdue' ? 'text-red-500' :
                            prequalification.callPriority === 'new'     ? 'text-amber-500' :
                            prequalification.callPriority === 'today'   ? 'text-blue-500' :
                            'text-slate-400'
                          }`}>
                            {prequalification.callPriority === 'new'
                              ? (
                                prequalification.uiType === 'Tender'
                                  ? (language === 'ur' ? 'نیا ٹینڈر - پہلا فالو اپ کریں' : 'New Tender - Make First Follow up')
                                  : prequalification.uiType === 'Proposal'
                                  ? (language === 'ur' ? 'نیا پروپوزل - پہلا فالو اپ کریں' : 'New Proposal - Make First Follow up')
                                  : prequalification.uiType === 'Profile'
                                  ? (language === 'ur' ? 'نیا پروفائل - پہلا فالو اپ کریں' : 'New Profile - Make First Follow up')
                                  : t('newLeadMakeFirstCall')
                                )
                              : prequalification.callStatus}
                          </p>

                          {friendlyOutcome && (
                            <p className="text-[11px] text-slate-400 mt-0.5">
                              {t('last')}: <span className="font-medium text-slate-500">{friendlyOutcome}</span>
                              {lastCallDateStr && <span className="text-slate-300"> · {lastCallDateStr}</span>}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Action buttons (Responsive Action Bar on Mobile) */}
                      <div className="flex items-center justify-between sm:justify-end gap-1.5 sm:gap-1.5 pt-2 sm:pt-0 border-t border-slate-100 sm:border-t-0 sm:border-none flex-shrink-0 w-full sm:w-auto">
                        {/* Call CTA - Prominent CTA on left/flex on mobile */}
                        <button
                          onClick={() => {
                            setSelectedLead(prequalification);
                            setShowLogModal(true);
                          }}
                          className={`flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3.5 py-2 sm:py-1.5 rounded-lg text-xs font-bold transition-all hover:scale-105 active:scale-95 shadow-sm ${
                            prequalification.callPriority === 'overdue'
                              ? 'bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-red-200'
                              : prequalification.callPriority === 'today'
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-blue-200'
                              : 'bg-gradient-to-r from-slate-700 to-slate-800 text-white shadow-slate-200'
                          }`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                          <span>{t('call')}</span>
                        </button>

                        {/* Add to calendar */}
                        {prequalification.nextCallDate && (
                          <button
                            onClick={() => openInGoogleCalendar(generateCallCalendarUrl(prequalification))}
                            title={t('addToCalendar')}
                            className="w-9 sm:w-8 h-9 sm:h-8 flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-indigo-100 hover:text-indigo-600 transition-all hover:scale-105 active:scale-95 shrink-0"
                          >
                            <CalendarPlus className="w-4 sm:w-3.5 h-4 sm:h-3.5" />
                          </button>
                        )}




                        {/* View details */}
                        <button
                          onClick={() => onSelectLead(prequalification)}
                          title={t('viewDetails')}
                          className="w-9 sm:w-8 h-9 sm:h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all hover:scale-105 active:scale-95 shrink-0"
                        >
                          <ChevronRight className="w-4 sm:w-4 h-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Load more */}
              {searchedList.length > displayLimit && (
                <div className="p-4 flex justify-center border-t border-slate-50">
                  <button
                    onClick={() => setDisplayLimit(prev => prev + 50)}
                    className="flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm hover:shadow-md"
                  >
                    <TrendingUp className="w-3.5 h-3.5" />
                    {language === 'ur' ? 'مزید لوڈ کریں' : 'Load more'}
                    <span className="px-1.5 py-0.5 bg-slate-100 rounded-md text-slate-500">
                      {searchedList.length - displayLimit}
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Log Call Modal ────────────────────────────────────────────────────── */}
      {showLogModal && selectedLead && (
        <LogCallModal
          prequalification={selectedLead}
          currentUser={currentUser}
          onClose={() => {
            setShowLogModal(false);
            setSelectedLead(null);
          }}
          onSave={onLogCall}
          onSaveLead={onSaveLead}
          venues={venues}
          eventTypes={eventTypes}
          managers={managers}
          isProfile={selectedLead.isProfile}
          onConvertProfile={onConvertProfile}
        />
      )}
    </div>
  );
}
